import * as angular from 'angular';
import L from 'leaflet';
import leaflet from 'leaflet';
import WKTParser from 'terraformer-wkt-parser';
import datacatalog from '../../../integration/datacatalog/datacatalog-services';
import {dataforvalterMapConfig} from '../../map/map-config';
import MapUtils from '../../map/map-utils';
import ColorHandler from './ColorHandler';
import {Feature} from "../../../integration/representation/feature";
import {Geometry} from "geojson";

export default function FeatureLayers(
    $q: angular.IQService,
    leafletMap: leaflet.Map,
    datacatalog: datacatalog,
    onFeatureClick: (featureId: string, feature: Feature, latlng: leaflet.LatLngExpression, map: leaflet.Map, datacatalog: datacatalog) => void,
    evaluateSelected: (feature: Feature) => boolean,
    showFeatureAttributesOnClick = false,
    cycleFeatureColors = false) {
    const self = this;

    const colorHandler = new ColorHandler(cycleFeatureColors);

    self.group = L.featureGroup();
    self.group.addTo(leafletMap);

    self.featureLayers = {};

    self.addWgs84Features = function(featureList: Feature[]) {
        addFeatureList(featureList, (feature: Feature) => {
            return MapUtils.swapLatLong(WKTParser.parse(feature.geometry.wkt));
        });
    };

    self.addFeatures = function(featureList: Feature[]) {
        addFeatureList(featureList, convertCoords);
    };

    function addFeatureList(featureList: Feature[], transformation: Function) {
        return $q(function(resolve){
            if (featureList.length === 0) return;

            featureList.forEach(function(feature) {
                const createdLayer = createLayerForFeature(leafletMap, feature, datacatalog, onFeatureClick, showFeatureAttributesOnClick, colorHandler, transformation(feature));
                if (createdLayer !== undefined) {
                    self.featureLayers[feature.id] = createdLayer;
                    self.group.addLayer(self.featureLayers[feature.id]);

                    if (evaluateSelected(feature)) {
                        self.featureLayers[feature.id].isHighlighted = true;
                        self.featureLayers[feature.id].renderHighlight();
                    }
                }

            });

            self.zoomToBounds();
            resolve();
        });
    }

    function convertCoords(feature: Feature) {
        if (!feature.geometry.wkt || feature.geometry.wkt.length === 0) {
            return;
        }

        const featurePosUtm33 = WKTParser.parse(feature.geometry.wkt);

        const featurePosition = MapUtils.convertUtm33ToWgs84(featurePosUtm33);
        return featurePosition;
    }

    self.removeFeatures = function(featureList: Feature[]) {
        featureList.forEach(function(feature) {
            self.group.removeLayer(self.featureLayers[feature.id]);
        });
        self.zoomToBounds();
    };

    self.updateFeature = function(feature: Feature, isSelected: boolean) {
        self.group.removeLayer(self.featureLayers[feature.id]);
        const createdLayer = createLayerForFeature(leafletMap, feature, datacatalog, onFeatureClick, showFeatureAttributesOnClick);
        if (createdLayer !== undefined) {
            self.featureLayers[feature.id] = createdLayer;
            self.featureLayers[feature.id].setHighlight(isSelected);
            self.group.addLayer(self.featureLayers[feature.id]);
        }
    };

    self.setHighlight = function(featureId: string, state: boolean) {
        if (self.featureLayers[featureId] !== undefined) {
            self.featureLayers[featureId].setHighlight(state);
        }
    };

    self.setSelected = function(featureId: string, state: boolean) {
        if (self.featureLayers[featureId] !== undefined) {
            self.featureLayers[featureId].setHighlight(state);
        }
    };

    self.zoomToBounds = function() {
        if (self.group.getBounds()._southWest) {
            leafletMap.fitBounds(self.group.getBounds().pad(dataforvalterMapConfig.PADBOUNDS_FACTOR));
        }
    };

    self.clear = function() {
        self.group.clearLayers();
    };

}

function renderHighlight(group: L.FeatureGroup, colorHandler: ColorHandler){
    group.getLayers().forEach(function(l) {
        (l as any).getLayers().forEach(function(subLayer: any){
            if (subLayer.setStyle){
                const groupColor = colorHandler.colorForFeatureType((group as any).featureTypeId);
                const color = (group as any).isHighlighted ? dataforvalterMapConfig.STYLES.highlightStyle4.color : groupColor;
                const opacity = (group as any).isHighlighted ?  dataforvalterMapConfig.STYLES.highlightStyle4.opacity : dataforvalterMapConfig.STYLES.defaultStyle.opacity;

                const currentRadius = subLayer.options && subLayer.options.radius ? subLayer.options.radius : 5;
                const radius = (group as any).isHighlighted ? currentRadius + 2 : currentRadius - 2;
                subLayer.setStyle({'color': color, 'fillColor': color, 'opacity': opacity, 'stroke-linecap': 'butt', 'radius': radius});
                if ((group as any).isHighlighted){
                    subLayer.bringToFront();
                    if (subLayer._path && subLayer._path.classList) {
                        subLayer._path.classList.add('marchingants');
                    } // setClass proposed but not implemented in leaflet. Fails on IE
                } else{
                    if (subLayer._path && subLayer._path.classList) {
                        subLayer._path.classList.remove('marchingants'); // removeClass proposed but not implemented in leaflet.
                    }
                }
            }
        });
    });
}

function createLayerForFeature(map: L.Map, feature: Feature, datacatalog: datacatalog, onFeatureClick: Function,
                               showFeatureAttributesOnClick: boolean, colorHandler?: ColorHandler, featurePosition?: Geometry): L.FeatureGroup {
    // Layer Group for feature
    const group = L.featureGroup() as any;
    group.featureId = feature.id;
    group.featureTypeId = feature.typeId;

    // Feature icon
    const featureLayer = L.geoJSON(
        featurePosition,
        {
            pointToLayer(mapfeature, latlng) {
                return L.circleMarker(latlng,
                    Object.assign(colorHandler.circleMarkerForFeatureType(group.featureTypeId), {className: 'pointMarker'}));
            },
            style() {
                return {
                    color: colorHandler.colorForFeatureType(group.featureTypeId),
                    opacity: dataforvalterMapConfig.STYLES.defaultStyle.opacity
                };
            }

        }
    );

    featureLayer.on('click', function(e: L.LeafletMouseEvent) {
        if (e.originalEvent.ctrlKey || e.originalEvent.metaKey) {
            if (showFeatureAttributesOnClick) {
                const properties = MapUtils.getFeaturePopupProps((feature as any), datacatalog);
                const popup = MapUtils.formatPopup(properties);
                L.popup()
                    .setLatLng(e.latlng)
                    .setContent(popup)
                    .openOn(map);
            }
        } else if (onFeatureClick) {
            onFeatureClick(feature.id, feature, e.latlng, map, datacatalog);
        }
    });

    group.selectablePosition = featurePosition; // This point is used when determining if this feature is contained in a bounding box

    group.addLayer(featureLayer);

    group.setHighlight = function(value: boolean) {
        group.isHighlighted = value;
        renderHighlight(group, colorHandler);
    };

    return group;
}
